
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  AlertCircle,
  Building2,
  CheckCircle2,
  ExternalLink,
  FileText,
  Globe,
  GraduationCap,
  Mail,
  MapPin,
  Phone,
  RefreshCw,
  School,
  ShieldCheck,
  Users,
} from "lucide-react";

import { api } from "../lib/api";

/* =========================================
   CONFIGURATION
========================================= */

const sectionOrder = [
  "Header",
  "Highlights",
  "Contact",
  "School Details",
  "Facilities",
  "Infrastructure",
  "Enrollment",
  "Teachers",
  "Management",
];

const sectionIcons = {
  "School Details": School,
  Facilities: CheckCircle2,
  Infrastructure: Building2,
  Enrollment: Users,
  Teachers: GraduationCap,
  Management: ShieldCheck,
};

const inputSections = [
  "Header",
  "Highlights",
  "Contact",
];

const fadeUp = {
  hidden: {
    opacity: 0,
    y: 18,
  },
  visible: {
    opacity: 1,
    y: 0,
  },
};

/* =========================================
   HELPERS
========================================= */

function byLabel(items, label, fallback = "") {
  return (
    items.find(
      (item) =>
        String(item.label || "").toLowerCase() ===
        label.toLowerCase()
    )?.value || fallback
  );
}

function getRecords(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) {
    return response.data.data;
  }
  return [];
}

function getContactIcon(label) {
  const value = String(label).toLowerCase();

  if (value.includes("email")) return Mail;
  if (value.includes("website")) return Globe;

  if (
    value.includes("phone") ||
    value.includes("mobile") ||
    value.includes("contact")
  ) {
    return Phone;
  }

  if (
    value.includes("address") ||
    value.includes("location")
  ) {
    return MapPin;
  }

  return FileText;
}

function getContactLink(item) {
  const label = String(item.label || "").toLowerCase();
  const value = String(item.value || "").trim();

  if (!value) return "";

  if (label.includes("email")) {
    return `mailto:${value}`;
  }

  if (
    label.includes("phone") ||
    label.includes("mobile") ||
    label.includes("contact")
  ) {
    return `tel:${value.replace(/[^\d+]/g, "")}`;
  }

  if (label.includes("website")) {
    if (/^https?:\/\//i.test(value)) {
      return value;
    }

    return `https://${value}`;
  }

  return "";
}

/* =========================================
   SHARED COMPONENTS
========================================= */

function DetailRow({ label, value }) {
  return (
    <div className="group flex flex-col gap-1 border-b border-slate-100 py-3 transition-colors last:border-b-0 hover:bg-red-50/20 sm:flex-row sm:items-start sm:justify-between sm:gap-5">
      <span className="text-[13px] font-normal leading-6 text-slate-500 sm:max-w-[52%]">
        {label}
      </span>

      <span className="break-words whitespace-pre-line text-[13px] font-medium leading-6 text-slate-800 sm:max-w-[48%] sm:text-right">
        {value || "—"}
      </span>
    </div>
  );
}

function SectionCard({
  title,
  icon: Icon,
  count,
  children,
  index = 0,
}) {
  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      variants={fadeUp}
      transition={{
        duration: 0.4,
        delay: Math.min(index * 0.05, 0.2),
      }}
      className="overflow-hidden rounded-[22px] border border-red-100 bg-white shadow-[0_10px_35px_rgba(80,30,30,0.055)]"
    >
      {/* Section Heading */}
      <div className="relative flex items-center justify-between gap-4 overflow-hidden border-b border-red-100 bg-gradient-to-r from-red-50/70 via-white to-amber-50/70 px-5 py-4 sm:px-6">
        <div className="pointer-events-none absolute -right-8 -top-12 h-28 w-28 rounded-full border-[18px] border-red-100/50" />

        <div className="relative flex min-w-0 items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-red-800 text-white">
            <Icon size={21} strokeWidth={1.8} />
          </div>

          <div>
            <h2 className="font-serif text-lg font-medium text-slate-900 sm:text-xl">
              {title}
            </h2>

            <p className="mt-0.5 text-[11px] font-normal text-slate-500">
              School information and details
            </p>
          </div>
        </div>

        <div className="relative hidden rounded-full border border-red-100 bg-white px-3 py-1.5 text-xs font-normal text-red-800 sm:block">
          {count} {count === 1 ? "Detail" : "Details"}
        </div>
      </div>

      {children}
    </motion.section>
  );
}

/* =========================================
   MAIN COMPONENT
========================================= */

export default function CbseDetail() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let active = true;

    async function loadDetails() {
      setLoading(true);
      setError("");

      try {
        const response = await api.activeCbseDetails();

        if (!active) return;

        const records = getRecords(response).filter(
          (item) => item.isActive !== false
        );

        setItems(records);
      } catch (requestError) {
        if (!active) return;

        setError(
          requestError?.message ||
            "Unable to load CBSE details."
        );
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadDetails();

    return () => {
      active = false;
    };
  }, [refreshKey]);

  const grouped = useMemo(() => {
    const groups = {};

    items.forEach((item) => {
      const section = item.section || "Other";

      if (!groups[section]) {
        groups[section] = [];
      }

      groups[section].push(item);
    });

    return groups;
  }, [items]);

  const header = grouped.Header || [];
  const highlights = grouped.Highlights || [];
  const contact = grouped.Contact || [];

  const contentSections = useMemo(() => {
    const sections = sectionOrder.filter(
      (section) =>
        !inputSections.includes(section) &&
        grouped[section]?.length
    );

    const otherSections = Object.keys(grouped).filter(
      (section) =>
        !sectionOrder.includes(section) &&
        grouped[section]?.length
    );

    return [...sections, ...otherSections];
  }, [grouped]);

  /* =========================================
     PAGE LAYOUT
  ========================================= */

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#FFFCF9] pb-12 text-slate-800">

      {/* BACKGROUND */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-32 top-72 h-80 w-80 rounded-full bg-red-100/50 blur-3xl" />

        <div className="absolute -right-32 bottom-20 h-96 w-96 rounded-full bg-amber-100/50 blur-3xl" />

        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage:
              "radial-gradient(#991b1b 1px, transparent 1px)",
            backgroundSize: "27px 27px",
          }}
        />
      </div>

      {/* PAGE HEADING */}
      <section className="relative overflow-hidden bg-gradient-to-r from-red-900 via-red-800 to-red-700 px-4 pb-20 pt-10 text-white sm:px-6 lg:px-8">
        <div className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full border-[35px] border-white/5" />

        <div className="pointer-events-none absolute bottom-0 left-1/2 h-40 w-40 rounded-full bg-amber-300/10 blur-3xl" />

        <div className="relative mx-auto max-w-7xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-white/10 px-4 py-1.5 text-[11px] font-normal tracking-wide text-amber-100">
            <GraduationCap size={16} />
            {byLabel(
              header,
              "Badge",
              "CBSE Affiliation Details"
            )}
          </span>

          <h1 className="mt-5 font-serif text-[28px] font-medium leading-tight sm:text-[37px] lg:text-[44px]">
            {byLabel(
              header,
              "School Name",
              "ST. XAVIER'S PUBLIC SCHOOL"
            )}
          </h1>

          <div className="mx-auto mt-4 h-1 w-20 rounded-full bg-amber-400" />

          <p className="mt-4 text-sm font-normal text-white/85 sm:text-base">
            {byLabel(
              header,
              "Subtitle",
              "CBSE Affiliation Information"
            )}
          </p>

          {byLabel(header, "Address") && (
            <p className="mx-auto mt-2 max-w-2xl text-[13px] leading-6 text-white/70">
              {byLabel(header, "Address")}
            </p>
          )}
        </div>
      </section>

      {/* CONTENT WRAPPER */}
      <div className="relative z-10 mx-auto -mt-11 max-w-7xl px-4 sm:px-6 lg:px-8">

        {/* LOADING STATE */}
        {loading ? (
          <div className="flex min-h-[320px] flex-col items-center justify-center rounded-2xl border border-red-100 bg-white p-6 shadow-sm">
            <RefreshCw
              size={30}
              className="animate-spin text-red-800"
            />

            <p className="mt-4 text-sm font-normal text-slate-600">
              Loading CBSE information...
            </p>
          </div>
        ) : error ? (
          <div className="mx-auto max-w-xl rounded-2xl border border-red-200 bg-white p-8 text-center shadow-sm">
            <AlertCircle
              size={32}
              className="mx-auto text-red-700"
            />

            <h2 className="mt-3 text-lg font-medium text-slate-900">
              Unable to Load Information
            </h2>

            <p className="mt-2 text-sm text-red-700">
              {error}
            </p>

            <button
              type="button"
              onClick={() =>
                setRefreshKey((key) => key + 1)
              }
              className="mt-5 inline-flex items-center gap-2 rounded-lg bg-red-800 px-5 py-2.5 text-sm font-normal text-white transition hover:bg-red-900"
            >
              <RefreshCw size={16} />
              Try Again
            </button>
          </div>
        ) : !items.length ? (
          <div className="rounded-2xl border border-red-100 bg-white px-6 py-12 text-center shadow-sm">
            <School
              size={42}
              className="mx-auto text-red-200"
            />

            <h2 className="mt-4 text-xl font-medium text-slate-900">
              CBSE Details
            </h2>

            <p className="mt-2 text-sm text-slate-500">
              CBSE information has not been published yet.
            </p>
          </div>
        ) : (
          <>
            {/* AFFILIATION HIGHLIGHTS */}
            {highlights.length > 0 && (
              <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {highlights.map((item, index) => {
                  const icons = [
                    School,
                    FileText,
                    GraduationCap,
                  ];

                  const Icon =
                    icons[index % icons.length];

                  return (
                    <motion.div
                      key={item._id || index}
                      initial={{ opacity: 0, y: 15 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 0.35,
                        delay: index * 0.05,
                      }}
                      className="group relative overflow-hidden rounded-2xl border border-red-100 bg-white px-5 py-5 shadow-[0_8px_30px_rgba(80,30,30,0.06)] transition-all hover:-translate-y-1 hover:shadow-lg"
                    >
                      <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-red-800 to-amber-400" />

                      <div className="flex items-start gap-4">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-800">
                          <Icon size={21} />
                        </div>

                        <div className="min-w-0">
                          <p className="text-xs font-normal text-slate-500">
                            {item.label}
                          </p>

                          <p className="mt-1 break-words text-xl font-medium leading-snug text-slate-900 sm:text-2xl">
                            {item.value || "—"}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}

            {/* CONTACT INFORMATION */}
            {contact.length > 0 && (
              <div className="mb-6 overflow-hidden rounded-2xl border border-red-100 bg-white shadow-[0_8px_30px_rgba(80,30,30,0.055)]">
                <div className="flex items-center gap-3 border-b border-red-100 bg-gradient-to-r from-red-50/60 to-amber-50/60 px-5 py-3.5">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-800 text-white">
                    <Phone size={17} />
                  </div>

                  <h2 className="font-serif text-lg font-medium text-slate-900">
                    Contact Information
                  </h2>
                </div>

                <div className="grid gap-0 sm:grid-cols-2 lg:grid-cols-3">
                  {contact.map((item, index) => {
                    const Icon = getContactIcon(item.label);
                    const url = getContactLink(item);

                    const isExternal =
                      /^https?:\/\//i.test(url);

                    return (
                      <div
                        key={item._id || index}
                        className="flex min-w-0 items-start gap-3 border-b border-slate-100 px-5 py-4 sm:border-r"
                      >
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-800">
                          <Icon size={18} />
                        </div>

                        <div className="min-w-0">
                          <p className="text-xs font-normal text-slate-500">
                            {item.label}
                          </p>

                          {url ? (
                            <a
                              href={url}
                              target={
                                isExternal
                                  ? "_blank"
                                  : undefined
                              }
                              rel={
                                isExternal
                                  ? "noopener noreferrer"
                                  : undefined
                              }
                              className="mt-1 inline-flex max-w-full items-start gap-1.5 break-all text-[13px] font-medium text-red-800 hover:underline"
                            >
                              {item.value}

                              {isExternal && (
                                <ExternalLink
                                  size={13}
                                  className="mt-1 shrink-0"
                                />
                              )}
                            </a>
                          ) : (
                            <p className="mt-1 break-words whitespace-pre-line text-[13px] font-medium leading-6 text-slate-800">
                              {item.value || "—"}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* DYNAMIC CONTENT SECTIONS */}
            <div className="space-y-6">
              {contentSections.map((section, index) => {
                const Icon =
                  sectionIcons[section] || School;

                const records = grouped[section] || [];

                const isFacilities =
                  section === "Facilities";

                return (
                  <SectionCard
                    key={section}
                    title={section}
                    icon={Icon}
                    count={records.length}
                    index={index}
                  >
                    {isFacilities ? (
                      <div className="grid gap-3 p-4 sm:grid-cols-2 sm:p-5 lg:grid-cols-3">
                        {records.map((item, itemIndex) => {
                          const value = String(
                            item.value || ""
                          ).trim();

                          const positive =
                            /^(yes|available|active|true)$/i.test(
                              value
                            );

                          return (
                            <div
                              key={item._id || itemIndex}
                              className="flex min-h-[72px] items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/70 px-4 py-3 transition hover:border-red-100 hover:bg-red-50/30"
                            >
                              <span className="text-[13px] font-normal leading-5 text-slate-600">
                                {item.label}
                              </span>

                              <span
                                className={`shrink-0 rounded-full px-3 py-1 text-xs font-normal ${
                                  positive
                                    ? "border border-emerald-200 bg-emerald-50 text-emerald-700"
                                    : "border border-slate-200 bg-white text-slate-600"
                                }`}
                              >
                                {value || "—"}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="grid gap-x-9 px-5 py-2 md:grid-cols-2 sm:px-6">
                        {records.map((item, itemIndex) => (
                          <DetailRow
                            key={item._id || itemIndex}
                            label={item.label}
                            value={item.value}
                          />
                        ))}
                      </div>
                    )}
                  </SectionCard>
                );
              })}
            </div>

            {/* BOTTOM NOTE */}
            <div className="mt-7 flex items-start gap-3 rounded-xl border-l-4 border-amber-400 bg-white px-5 py-4 shadow-sm">
              <ShieldCheck
                size={19}
                className="mt-0.5 shrink-0 text-amber-600"
              />

              <p className="text-xs font-normal leading-6 text-slate-600">
                CBSE affiliation and school information
                displayed on this page is maintained
                through the school administration portal.
                For further clarification, please contact
                the school office.
              </p>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
